import { lazy, Suspense } from 'react'
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from 'react-router-dom'
import GlobalProviders from '@/providers/global-providers'
import RootLayout from '@/app/[locale]/layout'
import TameeniLoaderOverlay from '@/components/tameeni-loader/tameeni-loader-overlay'
import LowercasePathRedirect from '@/components/lowercase-path-redirect'
import ShortUrlRedirect from '@/components/short-url-redirect'

// Pages — lazily imported
const PolicyholderDetailsPage = lazy(
  () => import('@/app/[locale]/(quoteRequest)/page')
)
const DeclarationsPage = lazy(
  () => import('@/app/[locale]/(quoteRequest)/declarations/page')
)
const ProfessionalInfoPage = lazy(
  () => import('@/app/[locale]/(quoteRequest)/professional-info/page')
)
const QuoteListPage = lazy(
  () => import('@/app/[locale]/(quoteRequest)/quote-list/page')
)
const SummaryAndPaymentPage = lazy(
  () => import('@/app/[locale]/(quoteRequest)/summary-and-payment/page')
)
const DashboardPage = lazy(() => import('@/app/[locale]/dashboard/page'))
const FaqPage = lazy(() => import('@/app/[locale]/faq/page'))
const DrsLoungePage = lazy(() => import('@/app/[locale]/home/drslounge/page'))
const PaymentResultPage = lazy(
  () => import('@/app/[locale]/payment/paymentsuccess/page')
)
const PolicyRenewalPage = lazy(
  () => import('@/app/[locale]/policy-renewal/page')
)
const PolicyDocumentPage = lazy(
  () => import('@/app/[locale]/policy-doc/[applicationReference]/page')
)
const PaymentReceiptPage = lazy(
  () => import('@/app/[locale]/receipt/[quotationReference]/page')
)
const LoginPage = lazy(() => import('@/app/[locale]/login/page'))
const InternalLoginPage = lazy(
  () => import('@/app/[locale]/internal-login/page')
)
const NotFoundPage = lazy(() => import('@/app/[locale]/not-found'))

// QuoteRequest layout wrapper (stepper)
const QuoteRequestLayout = lazy(
  () => import('@/app/[locale]/(quoteRequest)/layout')
)

// PolicyRenewal layout wrapper
const PolicyRenewalLayout = lazy(
  () => import('@/app/[locale]/policy-renewal/layout')
)

function RootRedirect() {
  const location = useLocation()
  const hasOidcCallback =
    location.search.includes('code=') || location.hash.includes('code=')
  if (hasOidcCallback) {
    return (
      <Navigate to={`/ar/login${location.search}${location.hash}`} replace />
    )
  }
  // Preserve the query string + hash so deep-link params (e.g. ANAT's
  // lang/id/yob/mob/dob/email/leanRefNum) survive the locale redirect instead
  // of being dropped on first render.
  return <Navigate to={`/ar${location.search}${location.hash}`} replace />
}

function LngWrapper() {
  return (
    <GlobalProviders>
      <RootLayout />
    </GlobalProviders>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <LowercasePathRedirect />
      <Suspense fallback={<TameeniLoaderOverlay transparent={false} />}>
        <Routes>
          {/* Short-url links (/sl/xxx) resolve on the backend — redirect there. */}
          <Route path='/sl/*' element={<ShortUrlRedirect />} />
          <Route path='/' element={<RootRedirect />} />
          <Route path='/:lng' element={<LngWrapper />}>
            {/* Auth */}
            <Route path='login' element={<LoginPage />} />
            <Route path='internal-login' element={<InternalLoginPage />} />

            {/* Quote request flow (with stepper layout) */}
            <Route element={<QuoteRequestLayout />}>
              <Route index element={<PolicyholderDetailsPage />} />
              <Route path='declarations' element={<DeclarationsPage />} />
              <Route
                path='professional-info'
                element={<ProfessionalInfoPage />}
              />
              <Route path='quote-list' element={<QuoteListPage />} />
              <Route
                path='summary-and-payment'
                element={<SummaryAndPaymentPage />}
              />
            </Route>

            {/* Dashboard */}
            <Route path='dashboard' element={<DashboardPage />} />

            {/* FAQ */}
            <Route path='faq' element={<FaqPage />} />

            {/* DRS Lounge */}
            <Route path='home/drslounge' element={<DrsLoungePage />} />

            {/* Payment result */}
            <Route
              path='payment/paymentsuccess'
              element={<PaymentResultPage />}
            />

            {/* Policy renewal (with side-content layout) */}
            <Route element={<PolicyRenewalLayout />}>
              <Route path='policy-renewal' element={<PolicyRenewalPage />} />
            </Route>

            {/* Policy document viewer */}
            <Route
              path='policy-doc/:applicationReference'
              element={<PolicyDocumentPage />}
            />

            {/* Payment receipt viewer */}
            <Route
              path='receipt/:quotationReference'
              element={<PaymentReceiptPage />}
            />

            {/* 404 */}
            <Route path='*' element={<NotFoundPage />} />
          </Route>
        </Routes>
      </Suspense>
    </BrowserRouter>
  )
}
